import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Building2, DollarSign, Package, Calendar, MoreVertical } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Order } from '@/types'
import { formatCurrency, formatDate } from '@/lib/utils'

interface MobileOrderCardProps {
  order: Order
}

function getStatusVariant(status?: string) {
  switch (status) {
    case 'Processed':
    case 'Approved':
      return 'success'
    case 'Pending':
      return 'warning'
    case 'Denied':
      return 'destructive'
    case 'Draft':
    default:
      return 'secondary'
  }
}

export function MobileOrderCard({ order }: MobileOrderCardProps) {
  const displayName = order.order_number || order.name || order.id

  return (
    <Card className="relative">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <Link
              to={`/orders/${order.id}`}
              className="font-semibold text-base hover:underline focus:underline focus:outline-none"
            >
              {displayName}
            </Link>
            {order.status && (
              <Badge variant={getStatusVariant(order.status)} className="mt-1 text-xs">
                {order.status}
              </Badge>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={`Actions for ${displayName}`}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={`/orders/${order.id}`}>View Details</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {order.account_name && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Building2 className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span className="truncate">{order.account_name}</span>
          </div>
        )}

        {order.total_amount !== undefined && order.total_amount !== null && (
          <div className="flex items-center gap-2">
            <DollarSign
              className="h-4 w-4 flex-shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
            <span className="font-medium">
              {formatCurrency(order.total_amount, order.currency_iso_code || 'CAD')}
            </span>
          </div>
        )}

        {order.item_count !== undefined && order.item_count !== null && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Package className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span>
              {order.item_count} item{order.item_count === 1 ? '' : 's'}
            </span>
          </div>
        )}

        {order.effective_date && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span>Effective: {formatDate(order.effective_date)}</span>
          </div>
        )}

        {order.created_date && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span className="text-xs">Created {formatDate(order.created_date)}</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface MobileOrderListProps {
  orders: Order[]
}

export function MobileOrderList({ orders }: MobileOrderListProps) {
  return (
    <div className="space-y-3">
      {orders.map((order) => (
        <MobileOrderCard key={order.id} order={order} />
      ))}
    </div>
  )
}
